import React from 'react';
import { Empty, Tag, Avatar, Timeline } from 'antd';
import {
  HistoryOutlined,
  UserOutlined,
  FileTextOutlined,
  FileOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  SwapOutlined,
  PhoneOutlined,
  EditOutlined,
  FundProjectionScreenOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { getHistoryEventMeta } from '../../../utils/historyEventLabels';
import { resolveAvatarSrc } from '../../../utils/avatarUtils';

const EVENT_ICONS = {
  'note-added': FileTextOutlined,
  'note-deleted': DeleteOutlined,
  'file-added': FileOutlined,
  'file-deleted': DeleteOutlined,
  'pipeline-updated': FundProjectionScreenOutlined,
  'followup-created': PhoneOutlined,
  'followup-deleted': DeleteOutlined,
  'stage-updated': SwapOutlined,
  'step-updated': SwapOutlined,
  'deal-updated': EditOutlined,
  'deal-created': PlusCircleOutlined,
  'deal-deleted': DeleteOutlined,
};

const getEventIcon = (eventType) => {
  const key = String(eventType || '').trim().toLowerCase();
  return EVENT_ICONS[key] || HistoryOutlined;
};

const HistoryTab = ({ dealId, data = [] }) => {
  const histories = data?.histories || [];

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return moment(dateString).format('jYYYY/jMM/jDD HH:mm');
    } catch {
      return null;
    }
  };

  if (!histories || histories.length === 0) {
    return (
      <div className="py-8">
        <Empty
          description="هیچ تاریخچه‌ای ثبت نشده است"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="pt-4 px-1 pb-4">
      <h3
        className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-5 m-0"
        style={{ fontFamily: 'CustomFont' }}
      >
        تاریخچه معامله
      </h3>
      <Timeline
        mode="left"
        style={{ fontFamily: 'CustomFont' }}
        items={histories.map((item, index) => {
          const meta = getHistoryEventMeta(item.event_type);
          const EventIcon = getEventIcon(item.event_type);
          const actorName = item.user?.name || item.causer?.name || item.created_by_name || 'سیستم';
          const formattedDate = formatDate(item.created_at);

          return {
            key: item.id || index,
            color: meta.color,
            dot: (
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border-2 shadow-sm"
                style={{ borderColor: `var(--ant-${meta.color}-6, #1677ff)` }}
              >
                <EventIcon className="text-sm" style={{ color: `var(--ant-${meta.color}-6, #1677ff)` }} />
              </div>
            ),
            label: formattedDate ? (
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap pt-1">
                {formattedDate}
              </span>
            ) : null,
            children: (
              <div className="mb-4 mr-2">
                <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm hover:shadow-md transition-shadow p-4">
                  <div className="flex items-start gap-3">
                    {(item.user || item.causer) && (
                      <Avatar
                        src={resolveAvatarSrc(item.user?.image_url || item.causer?.image_url)}
                        size={36}
                        icon={<UserOutlined />}
                        className="shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className="font-semibold text-gray-800 dark:text-gray-200 text-sm"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {actorName}
                        </span>
                        <Tag color={meta.color} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                          {meta.label}
                        </Tag>
                      </div>
                      {item.description && (
                        <p
                          className="text-sm text-gray-600 dark:text-gray-400 mb-0 mt-2 leading-relaxed"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ),
          };
        })}
      />
    </div>
  );
};

export default HistoryTab;
